import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const links = [
  { label: "Journal", href: "/journal", route: true },
  { label: "Nos solutions", href: "/#solutions" },
  { label: "Nos clients", href: "/#clients" },
  { label: "Contact", href: "/#audit" },
  { label: "Notre équipe", href: "/#equipe" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls =
    "group relative py-2 text-[13px] uppercase tracking-[0.18em] font-semibold text-[#f7f5f0]/85 hover:text-white transition-colors duration-300";

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.77, 0, 0.175, 1], delay: 0.3 }}
      className={`fixed top-0 left-0 right-0 z-50 bg-[#0a1e38] border-b border-white/10 transition-shadow duration-500 ${
        scrolled ? "shadow-lg shadow-[#0a1e38]/30" : ""
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <a
          href="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 font-serif-ed text-xl md:text-2xl tracking-tight text-[#f7f5f0]"
        >
          <img
            src="/logo-prosecours.png"
            alt="Logo ProSecours"
            className="h-8 md:h-9 w-auto"
          />
          <span>
            <span className="font-bold">Pro</span>
            <span className="text-[#ce1e20] font-bold italic">Secours</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) =>
            l.route ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={linkCls}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={linkCls}
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/#audit"
            data-testid="nav-cta-audit"
            className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-[#ce1e20] text-white overline hover:bg-[#a71518] transition-colors duration-300"
          >
            Décrire ma démarche
            <ArrowRight size={14} strokeWidth={1.8} />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-[#f7f5f0]"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-[#0a1e38] border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) =>
                l.route ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="font-serif-ed text-3xl text-[#f7f5f0]"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="font-serif-ed text-3xl text-[#f7f5f0]"
                  >
                    {l.label}
                  </a>
                )
              )}
              <a
                href="/#audit"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center gap-2 px-5 py-3 bg-[#ce1e20] text-white overline w-fit"
              >
                Décrire ma démarche →
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
